import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  FileText, Plus, Download, Save, ArrowLeft, Calendar,
  User, Package, CheckSquare, PenTool, Eye, Trash2
} from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import jsPDF from 'jspdf';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_REACT_APP_BACKEND_URL;

const HandoverProtocol = ({ onBack }) => {
  const [view, setView] = useState('list'); // 'list', 'create', 'view'
  const [protocols, setProtocols] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    handover_date: new Date().toISOString().slice(0, 16),
    employee_id: '',
    items: [],
    notes: ''
  });
  const [selectedItems, setSelectedItems] = useState([]);
  const [confirmationChecked, setConfirmationChecked] = useState(false);
  const sigCanvas = useRef(null);
  const [selectedProtocol, setSelectedProtocol] = useState(null);

  useEffect(() => {
    if (view === 'list') {
      fetchProtocols();
    } else if (view === 'create') {
      fetchEmployees();
      fetchInventoryItems();
    }
  }, [view]);

  const fetchProtocols = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${BACKEND_URL}/api/handover-protocols`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProtocols(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Protokolle:', error);
      alert('Fehler beim Laden der Protokolle');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${BACKEND_URL}/api/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data.filter(emp => emp.status === 'aktiv'));
    } catch (error) {
      console.error('Fehler beim Laden der Mitarbeiter:', error);
    }
  };

  const fetchInventoryItems = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${BACKEND_URL}/api/inventory?status=verfügbar`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInventoryItems(response.data);
    } catch (error) {
      console.error('Fehler beim Laden des Inventars:', error);
    }
  };

  const handleItemSelection = (itemId, checked) => {
    if (checked) {
      setSelectedItems([...selectedItems, itemId]);
    } else {
      setSelectedItems(selectedItems.filter(id => id !== itemId));
    }
  };

  const handleSave = async () => {
    // Validation
    if (!formData.employee_id) {
      alert('Bitte wählen Sie einen Mitarbeiter aus');
      return;
    }
    if (selectedItems.length === 0) {
      alert('Bitte wählen Sie mindestens einen Artikel aus');
      return;
    }
    if (!confirmationChecked) {
      alert('Bitte bestätigen Sie durch Setzen des Häkchens');
      return;
    }
    if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
      alert('Bitte fügen Sie eine Unterschrift hinzu');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      
      // Get signature data
      const signatureData = sigCanvas.current.toDataURL();
      
      const protocolData = {
        handover_date: new Date(formData.handover_date).toISOString(),
        employee_id: formData.employee_id,
        items: selectedItems,
        signature_data: signatureData,
        notes: formData.notes
      };

      await axios.post(`${BACKEND_URL}/api/handover-protocols`, protocolData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('Übergabeprotokoll erfolgreich erstellt');
      setView('list');
      resetForm();
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      alert('Fehler beim Speichern des Protokolls');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      handover_date: new Date().toISOString().slice(0, 16),
      employee_id: '',
      items: [],
      notes: ''
    });
    setSelectedItems([]);
    setConfirmationChecked(false);
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
  };

  const generatePDF = async (protocol) => {
    try {
      const pdf = new jsPDF();
      
      // Header
      pdf.setFontSize(20);
      pdf.text('SKALARTECH - Übergabeprotokoll', 20, 30);
      
      // Protocol info
      pdf.setFontSize(12);
      pdf.text(`Protokoll-Nr.: ${protocol.protocol_number}`, 20, 50);
      pdf.text(`Datum: ${new Date(protocol.handover_date).toLocaleDateString('de-DE')}`, 20, 60);
      pdf.text(`Mitarbeiter: ${protocol.employee_name} (${protocol.employee_number})`, 20, 70);
      
      // Items
      pdf.text('Übergebene Artikel:', 20, 90);
      let yPos = 100;
      protocol.items_details.forEach((item, index) => {
        pdf.text(`${index + 1}. ${item.name} (${item.category})`, 25, yPos);
        if (item.serial_number) {
          pdf.text(`   Seriennummer: ${item.serial_number}`, 25, yPos + 5);
          yPos += 5;
        }
        if (item.inventory_number) {
          pdf.text(`   Inventarnummer: ${item.inventory_number}`, 25, yPos + 5);
          yPos += 5;
        }
        yPos += 10;
      });
      
      // Confirmation text
      yPos += 10;
      pdf.text('Bestätigung:', 20, yPos);
      yPos += 10;
      const confirmationLines = pdf.splitTextToSize(protocol.confirmation_text, 170);
      pdf.text(confirmationLines, 20, yPos);
      yPos += confirmationLines.length * 5 + 10;
      
      // Signature
      if (protocol.signature_data) {
        pdf.text('Unterschrift:', 20, yPos);
        yPos += 10;
        try {
          pdf.addImage(protocol.signature_data, 'PNG', 20, yPos, 80, 30);
        } catch (e) {
          pdf.text('Unterschrift vorhanden', 20, yPos);
        }
      }
      
      // Notes
      if (protocol.notes) {
        yPos += 40;
        pdf.text('Notizen:', 20, yPos);
        yPos += 10;
        const notesLines = pdf.splitTextToSize(protocol.notes, 170);
        pdf.text(notesLines, 20, yPos);
      }
      
      pdf.save(`Übergabeprotokoll-${protocol.protocol_number}.pdf`);
    } catch (error) {
      console.error('Fehler beim PDF-Export:', error);
      alert('Fehler beim PDF-Export');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // List View
  if (view === 'list') {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button onClick={onBack} variant="outline" className="flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Zurück zur Lagerverwaltung
          </Button>
          <Button 
            onClick={() => setView('create')}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700"
          >
            <Plus className="w-4 h-4" />
            Neues Protokoll
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Übergabeprotokolle ({protocols.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Laden...</div>
            ) : protocols.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Noch keine Protokolle erstellt
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3">Protokoll-Nr.</th>
                      <th className="text-left p-3">Datum</th>
                      <th className="text-left p-3">Mitarbeiter</th>
                      <th className="text-left p-3">Anzahl Artikel</th>
                      <th className="text-left p-3">Status</th>
                      <th className="text-left p-3">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {protocols.map((protocol) => (
                      <tr key={protocol.id} className="border-b hover:bg-gray-50">
                        <td className="p-3 font-medium">{protocol.protocol_number}</td>
                        <td className="p-3">{formatDate(protocol.handover_date)}</td>
                        <td className="p-3">
                          {protocol.employee_name} 
                          <span className="text-gray-500 ml-1">({protocol.employee_number})</span>
                        </td>
                        <td className="p-3">{protocol.items.length}</td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            protocol.status === 'active' ? 'bg-green-100 text-green-800' :
                            protocol.status === 'returned' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {protocol.status === 'active' ? 'Aktiv' :
                             protocol.status === 'returned' ? 'Zurückgegeben' : 'Storniert'}
                          </span>
                        </td>
                        <td className="p-3">
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => {
                                setSelectedProtocol(protocol);
                                setView('view');
                              }}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => generatePDF(protocol)}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Create View
  if (view === 'create') {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button onClick={() => setView('list')} variant="outline" className="flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Zurück zur Liste
          </Button>
          <h2 className="text-xl font-bold">Neues Übergabeprotokoll</h2>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Übergabeprotokoll erstellen
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Date and Time */}
            <div>
              <label className="block text-sm font-medium mb-2">
                <Calendar className="w-4 h-4 inline mr-2" />
                Datum & Uhrzeit
              </label>
              <Input
                type="datetime-local"
                value={formData.handover_date}
                onChange={(e) => setFormData({...formData, handover_date: e.target.value})}
                className="w-full max-w-md"
              />
            </div>

            {/* Employee Selection */}
            <div>
              <label className="block text-sm font-medium mb-2">
                <User className="w-4 h-4 inline mr-2" />
                Mitarbeiter auswählen
              </label>
              <select
                value={formData.employee_id}
                onChange={(e) => setFormData({...formData, employee_id: e.target.value})}
                className="w-full max-w-md p-2 border border-gray-300 rounded-md"
              >
                <option value="">Bitte wählen...</option>
                {employees.map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.first_name} {employee.last_name} ({employee.employee_number})
                  </option>
                ))}
              </select>
            </div>

            {/* Items Selection */}
            <div>
              <label className="block text-sm font-medium mb-2">
                <Package className="w-4 h-4 inline mr-2" />
                Artikel auswählen ({selectedItems.length} ausgewählt)
              </label>
              <div className="border border-gray-300 rounded-md p-4 max-h-64 overflow-y-auto">
                {inventoryItems.length === 0 ? (
                  <p className="text-gray-500">Keine verfügbaren Artikel gefunden</p>
                ) : (
                  inventoryItems.map((item) => (
                    <label key={item.id} className="flex items-center space-x-3 py-2 hover:bg-gray-50 rounded">
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(item.id)}
                        onChange={(e) => handleItemSelection(item.id, e.target.checked)}
                        className="w-4 h-4 text-emerald-600 rounded"
                      />
                      <div className="flex-1">
                        <div className="font-medium">{item.name}</div>
                        <div className="text-sm text-gray-500">
                          {item.category} • {item.serial_number ? `SN: ${item.serial_number}` : 'Keine Seriennummer'}
                          {item.inventory_number && ` • INV: ${item.inventory_number}`}
                        </div>
                      </div>
                    </label>
                  ))
                )}
              </div>
            </div>

            {/* Confirmation Checkbox */}
            <div className="border border-gray-300 rounded-md p-4 bg-gray-50">
              <label className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  checked={confirmationChecked}
                  onChange={(e) => setConfirmationChecked(e.target.checked)}
                  className="w-4 h-4 text-emerald-600 rounded mt-1"
                />
                <div className="text-sm">
                  <CheckSquare className="w-4 h-4 inline mr-2" />
                  <strong>Bestätigung (Pflichtfeld):</strong>
                  <div className="mt-2 text-gray-700">
                    Ich bestätige, dass ich alle oben aufgeführten Werkzeuge und Materialien in einwandfreiem Zustand erhalten habe.
                    Alle Werkzeuge sind Eigentum der Firma SKALARTECH und müssen auf Aufforderung unverzüglich zurückgegeben werden.
                    Eine private Nutzung der Werkzeuge oder Materialien ist ausdrücklich untersagt.
                  </div>
                </div>
              </label>
            </div>

            {/* Digital Signature */}
            <div>
              <label className="block text-sm font-medium mb-2">
                <PenTool className="w-4 h-4 inline mr-2" />
                Digitale Unterschrift
              </label>
              <div className="border border-gray-300 rounded-md">
                <SignatureCanvas
                  ref={sigCanvas}
                  canvasProps={{
                    width: 400,
                    height: 150,
                    className: 'signature-canvas'
                  }}
                />
                <div className="p-2 bg-gray-50 border-t flex justify-end">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => sigCanvas.current?.clear()}
                  >
                    Löschen
                  </Button>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Notizen (optional)
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md"
                rows="3"
                placeholder="Zusätzliche Bemerkungen..."
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button 
                onClick={handleSave}
                disabled={loading}
                className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700"
              >
                <Save className="w-4 h-4" />
                {loading ? 'Speichern...' : 'Speichern'}
              </Button>
              <Button 
                variant="outline"
                onClick={() => setView('list')}
                disabled={loading}
              >
                Abbrechen
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // View Protocol Detail
  if (view === 'view' && selectedProtocol) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button onClick={() => setView('list')} variant="outline" className="flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Zurück zur Liste
          </Button>
          <div className="flex gap-2">
            <Button 
              onClick={() => generatePDF(selectedProtocol)}
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              PDF Export
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Übergabeprotokoll: {selectedProtocol.protocol_number}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <strong>Datum & Uhrzeit:</strong>
                <div>{formatDate(selectedProtocol.handover_date)}</div>
              </div>
              <div>
                <strong>Mitarbeiter:</strong>
                <div>{selectedProtocol.employee_name} ({selectedProtocol.employee_number})</div>
              </div>
            </div>

            <div>
              <strong>Übergebene Artikel ({selectedProtocol.items.length}):</strong>
              <ul className="list-disc ml-6 mt-2">
                {selectedProtocol.items_details.map((item, index) => (
                  <li key={index}>
                    {item.name} ({item.category})
                    {item.serial_number && ` - SN: ${item.serial_number}`}
                    {item.inventory_number && ` - INV: ${item.inventory_number}`}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <strong>Bestätigung:</strong>
              <div className="mt-2 p-3 bg-gray-50 rounded border text-sm">
                {selectedProtocol.confirmation_text}
              </div>
            </div>

            {selectedProtocol.signature_data && (
              <div>
                <strong>Unterschrift:</strong>
                <div className="mt-2 border rounded p-2 bg-white">
                  <img 
                    src={selectedProtocol.signature_data} 
                    alt="Unterschrift" 
                    className="max-w-md border"
                  />
                </div>
              </div>
            )}

            {selectedProtocol.notes && (
              <div>
                <strong>Notizen:</strong>
                <div className="mt-2 p-3 bg-gray-50 rounded border">
                  {selectedProtocol.notes}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
              <div>
                <strong>Status:</strong> {selectedProtocol.status === 'active' ? 'Aktiv' : selectedProtocol.status}
              </div>
              <div>
                <strong>Erstellt:</strong> {formatDate(selectedProtocol.created_at)}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
};

export default HandoverProtocol;